import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BookOpen, ArrowLeft, RefreshCw, Loader2,
  TrendingUp, Phone, MessageSquare, Target, Clock,
  AlertTriangle, CheckCircle, Users
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AIPlaybookInsights = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [playbook, setPlaybook] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchPlaybook();
  }, []);

  const fetchPlaybook = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.get(`${API}/api/ai-playbook/latest`, {
        params: { token, playbook_type: 'lead_qualification' }
      });
      
      if (response.data.success) {
        setPlaybook(response.data.playbook);
      }
    } catch (error) {
      console.error('Error fetching playbook:', error);
      // Try generating a new one if not found
      await regeneratePlaybook();
    } finally {
      setLoading(false);
    }
  };

  const regeneratePlaybook = async () => {
    try {
      setGenerating(true);
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.post(`${API}/api/ai-playbook/generate`, null, {
        params: { token, playbook_type: 'lead_qualification', force_refresh: true }
      });
      
      if (response.data.success) {
        setPlaybook(response.data.playbook);
        toast.success('Playbook generated successfully');
      }
    } catch (error) {
      console.error('Error generating playbook:', error);
      toast.error('Failed to generate playbook');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="ai-playbook-insights-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/ai-intelligence')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-purple-500" />
              AI Playbook Insights
            </h1>
            <p className="text-gray-500">Data-driven strategies from your sales patterns</p>
          </div>
        </div>
        <Button 
          onClick={regeneratePlaybook} 
          disabled={generating}
          data-testid="regenerate-btn"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
          ) : (
            <><RefreshCw className="w-4 h-4 mr-2" /> Regenerate</>
          )}
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Conversion Rate</p>
                <p className="text-2xl font-bold text-green-600">
                  {playbook?.benchmarks?.rates?.conversion_rate || 0}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Call Connect Rate</p>
                <p className="text-2xl font-bold text-blue-600">
                  {playbook?.benchmarks?.rates?.call_connect_rate || 0}%
                </p>
              </div>
              <Phone className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Best Channel</p>
                <p className="text-2xl font-bold capitalize text-purple-600">
                  {playbook?.response_patterns?.best_channel || 'N/A'}
                </p>
              </div>
              <MessageSquare className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Leads Analyzed</p>
                <p className="text-2xl font-bold text-orange-600">
                  {playbook?.data_points_analyzed?.contacts?.toLocaleString() || 0}
                </p>
              </div>
              <Target className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-5 w-full max-w-2xl mb-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="scripts">Scripts</TabsTrigger>
              <TabsTrigger value="objections">Objections</TabsTrigger>
              <TabsTrigger value="timing">Timing</TabsTrigger>
              <TabsTrigger value="sequences">Sequences</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-4">Recommended Action Sequence</h3>
                  <div className="space-y-3">
                    {(playbook?.sequences?.recommended_sequence || []).map((action, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-semibold">
                          {idx + 1}
                        </div>
                        <span className="capitalize">{action.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-4">Performance vs Industry</h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Conversion', value: playbook?.benchmarks?.rates?.conversion_rate, benchmark: playbook?.benchmarks?.industry_benchmarks?.real_estate_avg_conversion, color: 'blue' },
                      { label: 'Qualification', value: playbook?.benchmarks?.rates?.qualification_rate, benchmark: playbook?.benchmarks?.industry_benchmarks?.real_estate_avg_qualification, color: 'green' },
                      { label: 'Call Connect', value: playbook?.benchmarks?.rates?.call_connect_rate, benchmark: playbook?.benchmarks?.industry_benchmarks?.real_estate_avg_call_connect, color: 'purple' }
                    ].map((metric, idx) => (
                      <div key={idx}>
                        <div className="flex justify-between text-sm mb-1">
                          <span>{metric.label}</span>
                          <span>{metric.value || 0}% / {metric.benchmark}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full">
                          <div 
                            className={`h-2 bg-${metric.color}-500 rounded-full`}
                            style={{width: `${Math.min((metric.value / metric.benchmark) * 100, 100)}%`, backgroundColor: metric.color === 'blue' ? '#3b82f6' : metric.color === 'green' ? '#22c55e' : '#a855f7'}}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Scripts Tab */}
            <TabsContent value="scripts" className="space-y-6">
              <div>
                <h3 className="font-semibold mb-4">Greeting Scripts</h3>
                <div className="space-y-4">
                  {(playbook?.greeting_scripts || []).map((script, idx) => (
                    <div key={idx} className="border rounded-lg p-4 bg-slate-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{script.scenario}</Badge>
                        <Badge className="bg-purple-100 text-purple-800">{script.tone}</Badge>
                      </div>
                      <p className="text-gray-700 italic">"{script.script}"</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-4">Qualification Questions</h3>
                <div className="space-y-3">
                  {(playbook?.qualification_questions || []).map((q, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm flex-shrink-0">
                        {q.order}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{q.question}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(q.follow_ups || []).map((fu, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{fu}</Badge>
                          ))}
                        </div>
                      </div>
                      {q.required && <Badge className="bg-red-100 text-red-800">Required</Badge>}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-4">Closing Techniques</h3>
                <div className="space-y-4">
                  {(playbook?.closing_techniques || []).map((tech, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold">{tech.technique}</h4>
                        <Badge className="bg-green-100 text-green-800">{tech.success_rate}% success</Badge>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">When: {tech.when_to_use}</p>
                      <p className="text-gray-700 italic">"{tech.script}"</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Objections Tab */}
            <TabsContent value="objections" className="space-y-4">
              <h3 className="font-semibold mb-4">Objection Handlers</h3>
              {(playbook?.objection_handlers || []).map((obj, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    <h4 className="font-semibold text-red-600">"{obj.objection}"</h4>
                    <Badge variant="outline" className="ml-auto">{obj.category}</Badge>
                    <Badge className="bg-green-100 text-green-800">{obj.success_rate}%</Badge>
                  </div>
                  <div className="flex items-start gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700">{obj.response}</p>
                  </div>
                  <p className="text-sm text-gray-500">
                    Follow-up: <span className="font-medium">{obj.follow_up_action.replace(/_/g, ' ')}</span>
                  </p>
                </div>
              ))}
            </TabsContent>

            {/* Timing Tab */}
            <TabsContent value="timing" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Best Hours to Call
                  </h3>
                  <div className="space-y-2">
                    {(playbook?.timing_insights?.best_hours || []).map((h, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="font-medium">{h.hour}:00</span>
                        <span className="text-gray-500">{h.success_count} contacts</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-4">Best Days</h3>
                  <div className="space-y-2">
                    {(playbook?.timing_insights?.best_days || []).map((d, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="font-medium">{d.day}</span>
                        <span className="text-gray-500">{d.success_count} contacts</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-500">Optimal Window</p>
                  <p className="font-semibold text-green-700">
                    {playbook?.timing_insights?.recommendation?.optimal_call_window || 'N/A'}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-500">Best Day</p>
                  <p className="font-semibold text-blue-700">
                    {playbook?.timing_insights?.recommendation?.best_day || 'N/A'}
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-500">Avoid</p>
                  <p className="font-semibold text-red-700">
                    {playbook?.timing_insights?.recommendation?.avoid || 'N/A'}
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Sequences Tab */}
            <TabsContent value="sequences" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Winning Patterns</h3>
                <Badge variant="outline">
                  {playbook?.sequences?.sequences_analyzed || 0} sequences analyzed
                </Badge>
              </div>
              <div className="space-y-2">
                {(playbook?.sequences?.top_winning_patterns || []).map((pattern, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-mono text-sm">{pattern.pattern}</span>
                    <Badge variant="secondary">{pattern.frequency}x</Badge>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500">
        Last generated: {playbook?.generated_at_formatted ? new Date(playbook.generated_at_formatted).toLocaleString() : 'N/A'}
      </div>
    </div>
  );
};

export default AIPlaybookInsights;
